/* eslint-disable max-len */
import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormikProps } from 'formik';
import styled from 'styled-components';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  ErrorText,
  Input,
  Message,
  SidebarButton,
  SidebarFooter,
  SidebarHeaderTitle,
  SidebarList,
  SidebarListItem,
  SidebarListItemLabel,
  SidebarListItemValue,
  SidebarSubtitle,
  SidebarTermsFooter,
  SidebarTitle,
  TooltipIcon,
  TooltipWrapper,
  ValidationMessage,
} from 'components';
import { appConfig } from 'config/app';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { selectAPIRequestErrors } from 'store/ApiClient';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { Payment } from 'store/Payment';
import { selectSiteAcceptanceBalance, selectSiteSettings } from 'store/Site/selectors';
import { IStore } from 'store/types';
import { selectCurrentUser, selectCurrentUserFullName } from 'store/User';
import { DisbursementMethod, IApiErrorPayloadWithCode, IEmployeeSite, ITipAcceptancePaymentSetPayouts, PermissionType } from 'types';
import { centsToDollarsDisplay } from 'utils/currency';
import { hasSitePermission } from 'utils/employers';
import { checkTipAcceptancePayoutForErrors } from 'utils/payout';
import {
  BusinessDateInputWrapper,
  DescriptionFieldWrapper,
  DesktopDescription,
  MobileDescription,
  PaymentDescription,
  PRSContent,
  PRSHeader,
  PRSHeaderContent,
  PRSHeaderSection,
  SidebarActions,
  SidebarContentLayout,
  SidebarMobileActions,
  SidebarSection,
  SidebarSectionMobile,
} from '../components';
import { ITipAcceptanceValues } from './types';

interface IProps {
  formik: FormikProps<ITipAcceptanceValues>;
  hideHoldbacks?: boolean;
  isEditing: boolean;
  isProcessing: boolean;
  paymentSet: ITipAcceptancePaymentSetPayouts;
  sidebarOpen: boolean;
  siteId: string;
  onBackClick(): void;
  onConfirmEditsClick(values: ITipAcceptanceValues): void;
  onExpand(): void;
  onUndoEditsClick(): void;
}

const StyledMessage = styled(Message)`
  > &:not(:last-child) {
    margin-bottom: 20px;
  }
`;

const TipAcceptancePRSHeaderSection = styled(PRSHeaderSection)`
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
`;

const TipAcceptanceSidebarSubtitle = styled(SidebarSubtitle)`
  margin-bottom: 40px;
  font-size: ${toRem(16)};
  font-weight: bold;
  color: ${theme.color.neutral[400]};

  @media (max-width: ${theme.breakpoint.large}px) {
    display: none;
  }
`;

const TotalPayment = styled.span`
  display: flex;
  justify-content: space-between;

  & > span {
    font-weight: bold;

    &:first-child {
      margin-right: 5px;
    }
  }
`;

export const TipAcceptanceSideBar: React.FC<IProps> = ({ formik, hideHoldbacks, isEditing, isProcessing, paymentSet, sidebarOpen, siteId, onBackClick, onConfirmEditsClick, onExpand, onUndoEditsClick }) => {
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const currentUserName = useSelector(selectCurrentUserFullName());
  const { id: currentUserId } = useSelector(selectCurrentUser());
  const { perTipMax, rolePolicy } = useSelector(selectSiteSettings()) || {};
  const hasSubmitPaymentPermissions = hasSitePermission(currentSite?.permissions, PermissionType.PAYMENT_SUBMIT);
  const acceptanceBalance = useSelector(selectSiteAcceptanceBalance());
  const apiSubmitErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Payment.CREATE_PAYMENT_REQUEST)) as IApiErrorPayloadWithCode;

  const [showUndoEditsAlert, setShowUndoEditsModal] = useState(false);
  const [errorsCount, setErrorsCount] = useState(0);

  const totalToPay = useMemo(() => (isEditing ? formik?.values?.payouts : paymentSet?.payouts)?.reduce((acc, payout) => acc + payout.amount, 0), [isEditing, paymentSet, formik?.values?.payouts]);
  const totalHoldbacks = useMemo(() => (isEditing ? formik?.values?.payouts : paymentSet?.payouts)?.reduce((acc, curr) => acc + curr.holdbackAmount, 0) || 0, [isEditing, paymentSet, formik?.values?.payouts]);
  const remainderLeftToPay = useMemo(() => (acceptanceBalance || 0) - totalToPay, [paymentSet, totalToPay]);
  const totalPayrollTips = useMemo(
    () =>
      (isEditing ? formik?.values?.payouts : paymentSet?.payouts)?.reduce((acc, payout) => {
        const site = payout.employee.sites.find((site) => site.id === siteId);
        if (site?.disbursementMethod === DisbursementMethod.PAYROLL) {
          return acc + payout.amount;
        }

        return acc;
      }, 0) || 0,
    [isEditing, paymentSet, formik?.values?.payouts]
  );

  const totalInstantTips = useMemo(
    () =>
      (isEditing ? formik?.values?.payouts : paymentSet?.payouts)?.reduce((acc, payout) => {
        const site = payout.employee.sites.find((site) => site.id === siteId);
        if (site?.disbursementMethod === DisbursementMethod.INSTANT) {
          return acc + payout.amount;
        }

        return acc;
      }, 0) || 0,
    [isEditing, paymentSet, formik?.values?.payouts]
  );

  const changesMade = useMemo(() => {
    if (!isEditing) return false;

    const oldTotalToPay = formik?.initialValues?.payouts?.reduce((acc, payout) => acc + payout.amount, 0) || 0;
    const oldHoldbacks = formik?.initialValues.payouts?.reduce((acc, payout) => acc + payout.holdbackAmount, 0) || 0;
    let payoutNotesAdded = false;
    let jobChangeDetected = false;

    for (let i = 0; i < formik?.values?.payouts?.length; i++) {
      const editablePayout = formik?.values?.payouts[i];
      const origPayout = formik?.initialValues?.payouts?.length && i < formik?.initialValues?.payouts.length ? formik.initialValues.payouts[i] : null;

      // eslint-disable-next-line no-continue
      if (!origPayout) continue;

      if (editablePayout.notes !== origPayout.notes) {
        payoutNotesAdded = true;
      }

      const origEmpSite = origPayout.employee.sites.find((s) => s.id === currentSite.id);
      const updatedEmpSite = editablePayout.employee.sites.find((s) => s.id === currentSite.id);

      if (origEmpSite.job?.title !== updatedEmpSite.job?.title) {
        jobChangeDetected = true;
      }
    }

    return (
      totalToPay !== oldTotalToPay ||
      totalHoldbacks !== oldHoldbacks ||
      formik?.values?.payouts?.length !== paymentSet?.payouts?.length ||
      payoutNotesAdded ||
      jobChangeDetected ||
      formik?.initialValues?.description !== formik?.values?.description ||
      formik?.initialValues?.authorizedAgreement !== formik?.values?.authorizedAgreement
    );
  }, [isEditing, paymentSet, formik.initialValues, formik.values, totalToPay, totalHoldbacks]);

  const payoutLimitCalcs = useMemo(() => {
    const { value } = formik.values.payoutLimit;

    const limit = value || 0;
    const remaining = limit - totalToPay;
    const overage = remaining < 0 ? remaining : 0;
    return { limit, remaining, overage };
  }, [totalToPay, formik?.values?.payoutLimit]);

  const disableSubmittingEdits = useMemo(() => {
    const { remaining } = payoutLimitCalcs;

    return isProcessing || !changesMade || errorsCount > 0 || remainderLeftToPay < 0 || (formik?.values?.payoutLimit?.isStrict && remaining !== 0);
  }, [isProcessing, changesMade, errorsCount, remainderLeftToPay, formik?.values?.payoutLimit?.isStrict, payoutLimitCalcs]);

  const disableSubmittingPayment = useMemo(() => {
    const { remaining } = payoutLimitCalcs;

    return isProcessing || acceptanceBalance <= 0 || !formik.values.authorizedAgreement || !!errorsCount || !!formik.errors.businessDate || (formik.values.payoutLimit?.isStrict && remaining < 0) || !hasSubmitPaymentPermissions;
  }, [isProcessing, formik.values.authorizedAgreement, errorsCount, formik.errors.businessDate, formik.values.payoutLimit?.isStrict, payoutLimitCalcs, hasSubmitPaymentPermissions]);

  const disableTermsCheckbox = useMemo(() => {
    const { remaining } = payoutLimitCalcs;

    return isProcessing || acceptanceBalance <= 0 || !!errorsCount || !!formik.errors.businessDate || (formik.values.payoutLimit?.isStrict && remaining !== 0);
  }, [isProcessing, acceptanceBalance, errorsCount, formik.errors.businessDate, payoutLimitCalcs]);

  const onEditsBackClick = useCallback(() => {
    if (changesMade) {
      setShowUndoEditsModal(true);
    } else {
      onUndoEditsClick();
    }
  }, [changesMade, onUndoEditsClick]);

  const onTACancelUndoEditsClick = useCallback(() => {
    setShowUndoEditsModal(false);
  }, []);

  const onTAConfirmEditsClick = useCallback(() => {
    onConfirmEditsClick(formik.values);
  }, [formik.values]);

  const onTAUndoEditsClick = useCallback(() => {
    formik.resetForm();
    setShowUndoEditsModal(false);
    onUndoEditsClick();
  }, [onUndoEditsClick]);

  const showFieldAmount = (fields: number[]) => fields.filter((field) => typeof field === 'number').length === fields.length && ((isEditing && formik?.values?.payouts?.length > 0) || (!isEditing && paymentSet?.payouts?.length > 0));

  const renderAmounts = () => (
    <SidebarSection>
      <SidebarList>
        {!hideHoldbacks && (
          <SidebarListItem>
            <SidebarListItemLabel size="small" color="bluegray">
              <TooltipWrapper>
                Holdbacks
                <TooltipIcon id="holdback">Amount withheld to cover expenses</TooltipIcon>
              </TooltipWrapper>
            </SidebarListItemLabel>
            <SidebarListItemValue size="small" color="bluegray" data-cy="tip-acceptance-holdbacks">
              {showFieldAmount([totalHoldbacks]) ? `-${centsToDollarsDisplay(totalHoldbacks)}` : '--'}
            </SidebarListItemValue>
          </SidebarListItem>
        )}
        <SidebarListItem>
          <SidebarListItemLabel size="small" color="bluegray">
            Total
          </SidebarListItemLabel>
          <SidebarListItemValue size="small" color="bluegray" data-cy="tip-acceptance-total">
            {showFieldAmount([totalToPay, totalHoldbacks]) ? centsToDollarsDisplay(totalToPay - (hideHoldbacks ? 0 : totalHoldbacks)) : '--'}
          </SidebarListItemValue>
        </SidebarListItem>
        <SidebarListItem>
          <SidebarListItemLabel size="small" color="bluegray">
            Payroll
          </SidebarListItemLabel>
          <SidebarListItemValue size="small" color="bluegray" data-cy="tip-acceptance-payroll">
            {showFieldAmount([totalPayrollTips]) ? centsToDollarsDisplay(totalPayrollTips) : '--'}
          </SidebarListItemValue>
        </SidebarListItem>
        <SidebarListItem>
          <SidebarListItemLabel size="small" color="bluegray">
            Instant
          </SidebarListItemLabel>
          <SidebarListItemValue size="small" color="bluegray" data-cy="tip-acceptance-instant">
            {showFieldAmount([totalInstantTips]) ? centsToDollarsDisplay(totalInstantTips) : '--'}
          </SidebarListItemValue>
        </SidebarListItem>
        <SidebarListItem>
          <SidebarListItemLabel size="small">
            <strong>Pay Now</strong>
          </SidebarListItemLabel>
          <SidebarListItemValue size="small" data-cy="tip-acceptance-pay-now">
            <strong>{showFieldAmount([totalInstantTips]) ? centsToDollarsDisplay(totalInstantTips - (hideHoldbacks ? 0 : totalHoldbacks)) : '--'}</strong>
          </SidebarListItemValue>
        </SidebarListItem>
      </SidebarList>
    </SidebarSection>
  );

  const renderBusinessDay = () => (
    <SidebarListItem>
      <SidebarListItemLabel>Business Day</SidebarListItemLabel>
      <BusinessDateInputWrapper>
        {formik.values.businessDate ? (
          <>
            <DatePicker
              width={132}
              defaultValue={formik.values.businessDate}
              onBlur={() => formik.setFieldTouched('businessDate')}
              data-cy="tip-acceptance-sidebar-header-business-day"
              onChange={async (dateString: string) => {
                await formik.setFieldValue('businessDate', dateString);
                formik.setFieldTouched('businessDate');
              }}
            />
            {formik.touched.businessDate && !!formik.errors.businessDate && (
              <ValidationMessage data-cy="business-date-error" hasError>
                {formik.errors.businessDate || ''}
              </ValidationMessage>
            )}
          </>
        ) : (
          <div>--</div>
        )}
      </BusinessDateInputWrapper>
    </SidebarListItem>
  );

  const renderDescription = () => {
    if (isEditing) {
      return (
        <SidebarSection>
          <DescriptionFieldWrapper label="Description" optional>
            <Input
              placeholder="Description"
              data-cy="description-input"
              defaultValue={formik.values.description}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                formik.setFieldValue('description', e.target.value);
              }}
              maxLength={100}
            />
          </DescriptionFieldWrapper>
        </SidebarSection>
      );
    }

    return (
      <>
        <MobileDescription>
          <PaymentDescription data-cy="mobile-tip-acceptance-sidebar-description" description={formik.values.description} />
        </MobileDescription>
        <DesktopDescription>
          <PaymentDescription data-cy="desktop-tip-acceptance-sidebar-description" description={formik.values.description} />
        </DesktopDescription>
      </>
    );
  };

  const renderDiffAmounts = () => {
    if (!formik.values.payoutLimit?.isActive) return null;

    const { isActive, isStrict } = formik.values.payoutLimit;
    const { limit, remaining, overage } = payoutLimitCalcs;

    return (
      <SidebarSection>
        <SidebarList>
          <SidebarListItem>
            <SidebarListItemLabel size="small">
              <TooltipWrapper>
                <strong>Payout Limit</strong>
                <TooltipIcon id="payout-limit">This is the pool of funds you can use to pay employees</TooltipIcon>
              </TooltipWrapper>
            </SidebarListItemLabel>
            <SidebarListItemValue size="small" data-cy="tip-acceptance-payout-limit">
              <strong>{centsToDollarsDisplay(limit)}</strong>
            </SidebarListItemValue>
          </SidebarListItem>
          <SidebarListItem>
            <SidebarListItemLabel size="small">Remaining</SidebarListItemLabel>
            <SidebarListItemValue size="small" data-cy="tip-acceptance-remaining">
              {centsToDollarsDisplay(remaining > 0 ? remaining : 0)}
            </SidebarListItemValue>
          </SidebarListItem>
          {overage < 0 && (
            <SidebarListItem>
              <SidebarListItemLabel size="small" color="red">
                <strong>Over</strong>
              </SidebarListItemLabel>
              <SidebarListItemValue size="small" color="red" data-cy="tip-acceptance-overage">
                {`(${centsToDollarsDisplay(Math.abs(overage))})`}
              </SidebarListItemValue>
            </SidebarListItem>
          )}
          {overage < 0 && isActive && !isStrict && (
            <ErrorText size="small" data-cy="tip-pool-limit-warning">
              You have exceeded the payout limit. Increase the limit, or change your payouts.
            </ErrorText>
          )}
          {isStrict && remaining !== 0 && formik.values.payouts.length > 0 && (
            <ErrorText size="small" data-cy="tip-pool-limit-warning">
              {remaining > 0 ? 'You have not met the payout limit. Increase your payouts or decrease the limit.' : 'You have exceeded the payout limit. Increase the limit, or change your payouts.'}
            </ErrorText>
          )}
        </SidebarList>
      </SidebarSection>
    );
  };

  const renderErrors = () => {
    const errorsContent: { title: string; message: string; cy: string }[] = [];

    // No permissions to submit payments
    if (!hasSubmitPaymentPermissions) {
      errorsContent.push({
        title: 'Permission Denied',
        message: 'You do not have permissions to submit payments. Please contact Kickfin support.',
        cy: 'payment-submit-permissions-error',
      });
    }

    if (acceptanceBalance <= 0) {
      errorsContent.push({
        title: 'No Funds Warning',
        message: 'There are currently no funds available to pay these tips.',
        cy: 'overdraft-error',
      });
    } else if (remainderLeftToPay < 0) {
      errorsContent.push({
        title: 'Overdraft Warning',
        message: `The amount exceeds your balance by ${centsToDollarsDisplay(Math.abs(remainderLeftToPay))}. Edit your payments to be less than or equal to ${centsToDollarsDisplay(acceptanceBalance || 0)}.`,
        cy: 'overdraft-error',
      });
    }

    if (!isProcessing && !formik?.values?.payouts?.length) {
      errorsContent.push({
        title: 'No Payees Found',
        message: 'There must be at least 1 payee to continue.',
        cy: 'no-payees-error',
      });
    }

    if (acceptanceBalance > 0) {
      const payoutErrors: string[] = [];

      for (let i = 0; i < (formik?.values?.payouts || []).length; i += 1) {
        const payout = formik.values.payouts[i];

        const error = checkTipAcceptancePayoutForErrors(payout, currentSite as unknown as IEmployeeSite, payout.employee, rolePolicy, currentUserId, perTipMax);

        if (error) payoutErrors.push(error);
      }

      if (payoutErrors.length) {
        errorsContent.push({
          title: `${payoutErrors.length} Issue${payoutErrors.length > 1 ? 's' : ''} Detected`,
          message: `There ${payoutErrors.length > 1 ? 'are' : 'is'} ${payoutErrors.length} issue${payoutErrors.length > 1 ? 's' : ''} that must be corrected before submitting.`,
          cy: 'payout-error',
        });
      }
    }

    if (!errorsContent.length) {
      if (errorsCount) setErrorsCount(0);
      return null;
    }

    if (errorsCount !== errorsContent.length) setErrorsCount(errorsContent.length);

    const errors = errorsContent.map((error) => <StyledMessage key={error.title} dataCy={`tip-acceptance-sidebar-${error.cy}`} appearance={Appearance.WARNING} title={error.title} message={error.message} />);

    return <SidebarSection>{errors}</SidebarSection>;
  };

  const renderApiErrors = () =>
    apiSubmitErrors &&
    apiSubmitErrors?.errorMessages?.map((error: string, index: number) => <StyledMessage key={`error-message-${index}`} dataCy="tip-acceptance-sidebar-submit-error" appearance={Appearance.WARNING} title="Payment Error" message={error} />);

  const renderPayment = () => (
    <TotalPayment>
      <span>Pay Now:</span>
      <span data-cy="tip-acceptance-sidebar-header-pay-now-amount">{showFieldAmount([totalInstantTips - totalHoldbacks]) ? centsToDollarsDisplay(totalInstantTips - (hideHoldbacks ? 0 : totalHoldbacks)) : '--'}</span>
    </TotalPayment>
  );

  const renderSentBy = () => (
    <SidebarListItem>
      <SidebarListItemLabel>Sent By</SidebarListItemLabel>
      <SidebarListItemValue data-cy="tip-acceptance-sidebar-header-sent-by">{currentUserName}</SidebarListItemValue>
    </SidebarListItem>
  );

  return isEditing ? (
    <>
      <PRSHeader data-cy="tip-acceptance-sidebar-header">
        <TipAcceptancePRSHeaderSection>
          <TipAcceptanceSidebarSubtitle>Payment Summary</TipAcceptanceSidebarSubtitle>
          <SidebarHeaderTitle>
            <SidebarButton onClick={onExpand} isOpen={sidebarOpen} />
            <SidebarTitle title={renderPayment()} />
          </SidebarHeaderTitle>
          <SidebarMobileActions columns={2}>
            <Button label={changesMade ? 'Undo Edits' : 'Cancel'} appearance="white" size="small" disabled={false} onClick={onEditsBackClick} data-cy="tip-acceptance-mobile-undo-edits-button" />
            <Button label="Review Tips" appearance="primary" size="small" disabled={disableSubmittingEdits} isProcessing={isProcessing} onClick={onTAConfirmEditsClick} data-cy="tip-acceptance-mobile-submit-edits-button" />
          </SidebarMobileActions>
        </TipAcceptancePRSHeaderSection>
      </PRSHeader>

      <PRSContent data-cy="tip-acceptance-sidebar-content">
        {renderErrors()}
        {renderDiffAmounts()}
        {renderAmounts()}
        {renderDescription()}
      </PRSContent>

      <SidebarFooter data-cy="tip-acceptance-sidebar-footer">
        <SidebarActions>
          <Button label={changesMade ? 'Undo Edits' : 'Cancel'} appearance="white" size="large" disabled={false} onClick={onEditsBackClick} data-cy="tip-acceptance-undo-edits-button" />
          <Button label="Review Tips" appearance="primary" size="large" disabled={disableSubmittingEdits} isProcessing={isProcessing} onClick={onTAConfirmEditsClick} data-cy="tip-acceptance-submit-edits-button" />
        </SidebarActions>
      </SidebarFooter>
      {showUndoEditsAlert && (
        <Alert
          data-cy="undo-edits-alert"
          title="Are you sure you want to undo edits?"
          message="All changes to user tips will revert to original values."
          onLeftAction={onTACancelUndoEditsClick}
          onRightAction={onTAUndoEditsClick}
          leftActionLabel="Cancel"
          rightActionLabel="Undo Edits"
          isActive
        />
      )}
    </>
  ) : (
    <>
      <PRSHeader data-cy="tip-acceptance-sidebar-header">
        <PRSHeaderSection>
          <SidebarHeaderTitle>
            <SidebarTitle title={renderPayment()} />
          </SidebarHeaderTitle>
        </PRSHeaderSection>
        <PRSHeaderContent data-cy="sent-by">
          {renderBusinessDay()}
          {renderSentBy()}
        </PRSHeaderContent>
      </PRSHeader>

      <PRSContent data-cy="tip-acceptance-sidebar-content">
        <SidebarContentLayout>
          <SidebarSectionMobile>
            <SidebarList>
              {renderBusinessDay()}
              {renderSentBy()}
            </SidebarList>
          </SidebarSectionMobile>

          {renderErrors()}
          {renderDescription()}
          {renderAmounts()}
          {renderApiErrors()}
        </SidebarContentLayout>
      </PRSContent>

      <SidebarTermsFooter data-cy="tip-acceptance-sidebar-footer">
        <Checkbox
          onChange={async (e: ChangeEvent<HTMLInputElement>) => {
            await formik.setFieldValue('authorizedAgreement', e.target.checked);
            formik.setFieldTouched('authorizedAgreement');
          }}
          id="authorizedAgreement"
          name="authorizedAgreement"
          data-cy="authorized-agreement-checkbox"
          disabled={disableTermsCheckbox}
          checked={formik.values.authorizedAgreement}
          label={
            <span data-cy="authorized-agreement-text">
              I am authorized to make these transactions and aware of the{' '}
              <a href={appConfig.LINKS.TERMS_OF_USE} target="_blank" rel="noopener noreferrer">
                Terms and Conditions
              </a>{' '}
              to send payments.
            </span>
          }
        />
        <SidebarActions>
          <Button label="Back" appearance="white" size="large" disabled={isProcessing} onClick={onBackClick} data-cy="tip-acceptance-back-btn" />
          <Button type="submit" label="Submit" appearance="primary" size="large" data-cy="tip-acceptance-submit-btn" disabled={disableSubmittingPayment} isProcessing={isProcessing} />
        </SidebarActions>
      </SidebarTermsFooter>
    </>
  );
};
